/**
 * Auth store using Zustand.
 * Passkey-only: WebAuthn login/registration plus recovery-code fallback.
 * The session token lives in an httpOnly cookie the server sets — page JS
 * never sees it; only the user object is cached here (for offline-open).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserPrivate } from '../types';
import { api } from '../api/client';
import {
  authenticateWithPasskey,
  registerWithPasskey,
  isWebAuthnSupported,
  useRecoveryCode,
} from '../api/passkey';
import { useCameraStore } from './cameraStore';
import { sendEngine } from '../services/queue/engine';
import { drafts } from '../services/media/drafts';
import { mediaStore } from '../services/media/mediaStore';
import { momentPrefetcher } from '../services/momentPrefetcher';
import { receiveEngine } from '../services/inbox/receiveEngine';
import { receiveStore } from '../services/inbox/receiveStore';
import { inRecoveryMode } from '../utils/bootGuard';

/**
 * Signing in is what starts the queue (BUG-069): moments and Memories saves
 * that were waiting on a session go now, by themselves.
 *
 * It is also the ONLY thing that starts it. Opening the app loads the ledger
 * and touches nothing else — no network, no media — because every boot-path
 * shortcut this app has taken has eventually cost it a launch.
 */
function resumeQueuedWork(userId?: number): void {
  // A moment the wave-2 upgrade could not attribute to anyone (the retired
  // outbox kept no owner) belongs to whoever signs in on this phone.
  if (userId !== undefined) void drafts.adoptOwnerless(userId);
  // Ask, every launch, for the unsent moments on this phone to survive a
  // storage squeeze — they exist nowhere else (FEAT-038 wave 3). The browser
  // may refuse; re-asking is free and the answer can change.
  void mediaStore.requestPersistence();
  sendEngine.kick();
  // The offline inbox resumes with the session too (FEAT-040 w3): a shared
  // phone sheds the previous account's received bytes FIRST — the engine
  // downloads every pending row it sees, whoever's they are — then downloads
  // continue. A recovery boot skips all of it: downloads are exactly the
  // optional launch work the boot guard exists to shed.
  if (userId !== undefined && !inRecoveryMode()) {
    void receiveStore
      .shredOtherUsers(userId)
      .catch(() => {})
      .then(() => receiveEngine.kick());
  }
}

interface AuthState {
  // State
  user: UserPrivate | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  recoveryCodes: string[] | null; // Shown once after registration
  /** The session died while the app was open — the sign-in screen says so
   * instead of pretending this is a first visit (BUG-069). */
  sessionExpired: boolean;

  // Actions
  loginWithPasskey: () => Promise<boolean>;
  loginWithRecoveryCode: (username: string, code: string) => Promise<boolean>;
  registerWithInvite: (inviteCode: string, deviceName?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  noteSessionExpired: () => void;
  clearError: () => void;
  clearRecoveryCodes: () => void;
  isPasskeySupported: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      recoveryCodes: null,
      sessionExpired: false,

      // A 401 came back from a normal request: the session is gone. Keep the
      // cached user so the sign-in screen can greet them by name.
      noteSessionExpired: () => {
        if (!get().isAuthenticated) return;
        // Prefetched moment bytes are session content (FEAT-028 wave 6)
        momentPrefetcher.clearAll();
        set({ isAuthenticated: false, sessionExpired: true });
      },

      // Check if passkey is supported
      isPasskeySupported: (): boolean => {
        return isWebAuthnSupported();
      },

      // Clear recovery codes (after user has saved them)
      clearRecoveryCodes: () => set({ recoveryCodes: null }),

      // Login with passkey (primary method)
      loginWithPasskey: async (): Promise<boolean> => {
        set({ isLoading: true, error: null });

        const result = await authenticateWithPasskey();

        if (result.success && result.data) {
          const { user } = result.data;
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            sessionExpired: false,
          });
          resumeQueuedWork(user.id);
          return true;
        }

        set({
          isLoading: false,
          error: result.error?.message || "Sign-in didn't work. Try again.",
        });
        return false;
      },

      // Register with invite code (new users)
      registerWithInvite: async (inviteCode: string, deviceName?: string): Promise<boolean> => {
        set({ isLoading: true, error: null });

        const result = await registerWithPasskey(inviteCode, deviceName);

        if (result.success && result.data) {
          const { user, recovery_codes } = result.data;
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            sessionExpired: false,
            recoveryCodes: recovery_codes, // Show these to user once
          });
          resumeQueuedWork(user.id);
          return true;
        }

        set({
          isLoading: false,
          error: result.error?.message || 'Registration failed',
        });
        return false;
      },

      // Login with a one-time recovery code (lost passkey fallback)
      loginWithRecoveryCode: async (username: string, code: string): Promise<boolean> => {
        set({ isLoading: true, error: null });

        const result = await useRecoveryCode(username.trim(), code.trim());

        if (result.success && result.data) {
          const { user } = result.data;
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            sessionExpired: false,
          });
          resumeQueuedWork(user.id);
          return true;
        }

        set({
          isLoading: false,
          error: result.error?.message || 'Invalid username or recovery code',
        });
        return false;
      },

      // Logout
      logout: async (): Promise<void> => {
        // Prefetched moment bytes go (FEAT-028 wave 6) — no cross-account
        // leftovers, ever. Queued sends deliberately DO survive: they are this
        // device's unsent moments, and BUG-069's promise is that signing back
        // in finishes them rather than that signing out throws them away.
        momentPrefetcher.clearAll();
        // The offline inbox goes with them (FEAT-040 w3): received bytes are
        // session content. (noteSessionExpired deliberately does NOT do this —
        // a 401 blip is the same person, and re-downloading 300MB is not free
        // the way re-warming a prefetch is.)
        void receiveStore.clearAll().catch(() => {});

        // Server invalidates the session and clears the cookie
        await api.post('/auth/logout');

        // Destroy camera stream on logout (this is when we truly want to kill it)
        useCameraStore.getState().destroyStream();

        set({
          user: null,
          isAuthenticated: false,
          error: null,
          sessionExpired: false,
        });
      },

      // Check if the cookie session is still valid
      checkAuth: async (): Promise<boolean> => {
        set({ isLoading: true });

        const response = await api.get<UserPrivate>('/auth/me', { timeout: 10000 });

        if (response.success && response.data) {
          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
            sessionExpired: false,
          });
          resumeQueuedWork(response.data.id);
          return true;
        }

        // Offline, flaky signal, or the SERVER having trouble (5xx — e.g. a
        // Redis blip, BUG-042): keep the cached session so the app still
        // opens. Only a definitive rejection (401/403) logs out.
        const code = response.error?.code;
        const status = response.error?.status ?? 0;
        if (
          (code === 'NETWORK_ERROR' ||
            code === 'TIMEOUT' ||
            status >= 500 ||
            !navigator.onLine) &&
          get().user
        ) {
          set({ isAuthenticated: true, isLoading: false });
          return true;
        }

        // Session invalid, clear auth state
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return false;
      },

      // Clear error message
      clearError: () => set({ error: null }),
    }),
    {
      name: 'momento-auth',
      partialize: (state) => ({
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        // A cached user means a probably-live cookie session; checkAuth
        // confirms with the server once the app is up.
        if (state?.user) {
          state.isAuthenticated = true;
        }
      },
    }
  )
);
